import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Header, Table, Button, Icon, Message, Modal, Input, Field } from 'react-frontier';
import { useBreadcrumbs, useUser } from '../Hooks';
import { bindClick, bindFormChange, formatUnix } from '../SiteUtil';
import { Asset, AssetType, SetLoading } from '../SiteClasses';
import UserAccess from '../UserAccess';
import UploadBox, { UploadBoxRef } from '../components/UploadBox';
import Toast from '../Toast';
import API from '../API';

function fileType(file: File): AssetType | null{
	if(file.type.startsWith('image/')) return AssetType.IMAGE;
	if(file.type === 'application/pdf') return AssetType.PDF;
	return null;
}

var typeName = (t: AssetType)=> t===AssetType.IMAGE ? 'Imagen' : t===AssetType.PDF ? 'PDF' : 'Archivo';
var typeIcon = (t: AssetType)=> t===AssetType.IMAGE ? 'image' : t===AssetType.PDF ? 'file-pdf' : 'file';

interface AssetForm{
	asset_name: string,
	description: string,
	type_id: AssetType,
	file: File,
}

var PromoterAssets = ()=>{
	var params = useParams<{ prospect_id?: string, promoter_id?: string }>();
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var isProspect = !!params.prospect_id;
	var id = parseInt(params.prospect_id || params.promoter_id);
	var target = isProspect ? { prospect_id: id } : { promoter_id: id };
	var canEdit = hasAccess(isProspect ? UserAccess.PROSPECTS.EDIT_PROSPECTS : UserAccess.PROMOTERS.EDIT_PROMOTERS);
	var uploadRef = useRef<UploadBoxRef>(null);

	var [assets, setAssets] = useState<Asset[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [addOpen, setAddOpen] = useState<boolean>(false);
	var [form, setForm] = useState<AssetForm>(null);
	var [prompts, setPrompts] = useState<string[]>(null);

	var load = ()=>{
		setLoadError(null);
		API.getAssets(target).then(res=>{
			if(res.error) return setLoadError(res.message);
			setAssets(res.data || []);
		}).catch(()=>setLoadError('Hubo un error cargando las evidencias. (LCL-1)'));
	}

	useEffect(()=>{ setTitle('Evidencias'); }, []);
	useEffect(load, [id, isProspect]);

	var openAdd = ()=>{
		setPrompts(null);
		setForm({ asset_name: '', description: '', type_id: null, file: null });
		setAddOpen(true);
	}

	var onFile = (file: File | null)=>{
		if(!file) return setForm(f=>({ ...f, file: null, type_id: null }));
		var type_id = fileType(file);
		if(!type_id){
			uploadRef.current?.clear();
			return Toast.error('Tipo de archivo no soportado. Usa imagen o PDF.');
		}
		setForm(f=>({ ...f, file, type_id, asset_name: f.asset_name || file.name.slice(0, 128) }));
	}

	var submit = (setLoading: SetLoading)=>{
		if(!form.asset_name.trim()) return setPrompts(['Escribe un título.']);
		if(!form.file) return setPrompts(['Selecciona un archivo.']);

		setLoading(true);
		API.uploadAsset(target, form.file, form.asset_name.trim(), form.type_id, form.description.trim() || undefined).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Evidencia subida');
			setAddOpen(false);
			load();
		}).catch(()=>setPrompts(['Hubo un error subiendo la evidencia. (LCL-1)']))
		.finally(()=>setLoading(false));
	}

	var remove = (asset: Asset)=>{
		if(!window.confirm(`¿Eliminar "${asset.asset_name}"?`)) return;
		API.deleteAsset(asset.asset_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			setAssets(prev=>prev.filter(a=>a.asset_id!==asset.asset_id));
		}).catch(()=>Toast.error('Hubo un error eliminando la evidencia. (LCL-1)'));
	}

	if(loadError) return <Header text='Error' subtext={loadError} />;

	var onFormChange = form && bindFormChange(form, setForm);

	return <div style={{ paddingTop: 20 }}>
		<Table fitted striped style={{ maxWidth: 800, margin: '0 auto' }}>
			<Table.Head
				title='Evidencias'
				titleRight={canEdit ? <Button iconName='plus' color='black' text='Agregar' size='small' onClick={openAdd} /> : undefined}
				data={[['ID', 'Título', 'Creador', 'Fecha', '']]}
				collapsingIndexes={[0, 3, 4]}
			/>
			<Table.Body>
				{!assets ? (
					<Table.Row><Table.Cell colSpan={5} centered><Header loading text='Cargando datos' /></Table.Cell></Table.Row>
				) : assets.length===0 ? (
					<Table.Row empty><Table.Cell colSpan={5} centered>No hay evidencias.</Table.Cell></Table.Row>
				) : assets.map(a=>(
					<Table.Row key={a.asset_id}>
						<Table.Cell collapsing>{a.asset_id}</Table.Cell>
						<Table.Cell><Icon name={typeIcon(a.type_id)} /> {a.asset_name}</Table.Cell>
						<Table.Cell>{a.user_creator_name}</Table.Cell>
						<Table.Cell collapsing>{formatUnix(a.date_created, 'dd/MMM/yyyy')}</Table.Cell>
						<Table.Cell collapsing centered className='no-select'>
							<Button basic icon iconName='ellipsis' size='small' menu={[
								{ text: 'Ver', iconName: 'eye', onClick: ()=>window.open(a.url, '_blank') },
								{ text: 'Eliminar', iconName: 'trash', if: canEdit, onClick: ()=>remove(a) },
							]} />
						</Table.Cell>
					</Table.Row>
				))}
			</Table.Body>
		</Table>

		<Modal open={addOpen} onClose={bindClick(setAddOpen, false)} size='tiny'>
			<Modal.Header text='Agregar evidencia' />
			{!!form && (
				<Modal.Content>
					<Input label='Título' value={form.asset_name} onChange={onFormChange('asset_name')} fluid/>
					<Input textarea label='Descripción' value={form.description} onChange={onFormChange('description')} fluid/>
					<div style={{ textAlign: 'center', alignItems: 'center'}}>
						<Field label='Archivo' comment='Adjunta una imagen o PDF'>
							<UploadBox
								ref={uploadRef}
								icon={form.file ? typeIcon(form.type_id) : 'upload'}
								text={form.file ? form.file.name : null}
								accept='image/*,application/pdf'
								maxFileSize={8*1024*1024}
								onFile={onFile}
							/>
						</Field>
					</div>
					<Message type='error' list={prompts} />
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setAddOpen, false)} />
				<Button text='Guardar' color='black' size='small' onClick={submit} />
			</Modal.Actions>
		</Modal>
	</div>;
}

export default PromoterAssets;
